/**
 * Feature guide (功能说明书): the short in-app guide shown on every feature page. Content lives in
 * docs/help/guide/<id>.md (one file per page / tab, en + zh) — the single source; the full tutorial stays the help
 * article named by `help:`. Format: tasks/.feature-guide-format.md. This file parses and filters it (web + tests).
 */
export const GUIDE_LANGS = ["en", "zh"] as const;
export type GuideLang = (typeof GUIDE_LANGS)[number];
export const GUIDE_SECTIONS = ["what", "steps", "settings", "tips"] as const;
export type GuideSection = (typeof GUIDE_SECTIONS)[number];
/** line tags: country (shop country or a location's tax regime), web / store app only, owners + managers only */
export const GUIDE_TAGS = ["US", "GB", "MY", "SG", "web", "app", "owner"] as const;
export type GuideTag = (typeof GUIDE_TAGS)[number];
export const GUIDE_ADDONS = ["packages", "inventory", "payroll", "website", "shop", "suppliers"] as const;

export interface GuideLine { text: string; tags: GuideTag[] }
export type GuideBody = Record<GuideSection, GuideLine[]>;
export interface GuideEntry {
  id: string; routes: string[]; help: string; plan: string;
  title: Record<GuideLang, string>;
  body: Record<GuideLang, GuideBody>;
}
export type GuidePlan = { kind: "base" } | { kind: "full" } | { kind: "addon"; feature: string };

const LANG_MARK = /^<!--\s*lang:(en|zh)\s*-->\s*$/;
const TAG_RE = /^\[([A-Za-z]+(?:\s*,\s*[A-Za-z]+)*)\]\s*/;

function splitTags(raw: string): GuideLine {
  const m = TAG_RE.exec(raw);
  if (!m) return { text: raw.trim(), tags: [] };
  const tags = m[1]!.split(",").map((s) => s.trim());
  if (!tags.every((t) => (GUIDE_TAGS as readonly string[]).includes(t))) return { text: raw.trim(), tags: [] };
  return { text: raw.slice(m[0].length).trim(), tags: tags as GuideTag[] };
}

const emptyBody = (): GuideBody => ({ what: [], steps: [], settings: [], tips: [] });

export function parseGuide(src: string): GuideEntry {
  const text = src.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(text);
  if (!m) throw new Error("guide: missing front matter");
  const fm: Record<string, string> = {};
  for (const line of m[1]!.split("\n")) { const i = line.indexOf(":"); if (i > 0) fm[line.slice(0, i).trim()] = line.slice(i + 1).trim(); }
  const id = fm.id ?? "";
  if (!/^[a-z0-9-]{2,40}$/.test(id)) throw new Error("guide: bad id " + id);
  const body: Record<GuideLang, GuideBody> = { en: emptyBody(), zh: emptyBody() };
  let lang: GuideLang | null = null;
  let sec: GuideSection | null = null;
  let last: GuideLine | null = null;
  for (const line of m[2]!.split("\n")) {
    const lm = LANG_MARK.exec(line.trim());
    if (lm) { lang = lm[1] as GuideLang; sec = null; last = null; continue; }
    if (!lang) continue;
    const h = /^##\s+(\w+)\s*$/.exec(line.trim());
    if (h) { const k = h[1]!.toLowerCase(); sec = (GUIDE_SECTIONS as readonly string[]).includes(k) ? (k as GuideSection) : null; last = null; continue; }
    if (!sec || !line.trim()) { last = null; continue; }
    const item = /^\s*(?:\d+[.)]|[-*])\s+(.*)$/.exec(line);
    if (item) { last = splitTags(item[1]!); body[lang][sec].push(last); continue; }
    if (sec === "what" && !last) { last = splitTags(line); body[lang][sec].push(last); continue; }
    if (last) last.text += " " + line.trim(); else { last = splitTags(line); body[lang][sec].push(last); }
  }
  const list = (v?: string) => (v ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  return {
    id, routes: list(fm.routes), help: fm.help ?? "", plan: fm.plan ?? "base",
    title: { en: fm["title.en"] ?? "", zh: fm["title.zh"] ?? fm["title.en"] ?? "" },
    body,
  };
}

export function guidePlan(plan: string): GuidePlan {
  if (plan === "full") return { kind: "full" };
  const a = /^addon:([a-z_]+)$/.exec(plan);
  if (a) return { kind: "addon", feature: a[1]! };
  return { kind: "base" };
}

/** Problems that make an entry incomplete (the coverage test fails on any). */
export function guideProblems(e: GuideEntry): string[] {
  const out: string[] = [];
  if (!e.routes.length) out.push("no routes");
  if (!e.help) out.push("no help article");
  if (!/^(base|full|addon:[a-z_]+)$/.test(e.plan)) out.push("bad plan " + e.plan);
  const p = guidePlan(e.plan);
  if (p.kind === "addon" && !(GUIDE_ADDONS as readonly string[]).includes(p.feature)) out.push("unknown add-on " + p.feature);
  for (const l of GUIDE_LANGS) {
    if (!e.title[l]) out.push(l + ": no title");
    for (const s of GUIDE_SECTIONS) if (!e.body[l][s].length) out.push(l + ": empty " + s);
  }
  return out;
}

// ---------------------------------------------------------------- routes
/** "/owner/pos/:orderId/pay#sub" → { path regex, sub } */
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function routeParts(route: string): { re: RegExp; sub: string | null } {
  const [path, sub] = route.split("#") as [string, string | undefined];
  const segs = path.replace(/\/$/, "").split("/").map((seg) => (seg.startsWith(":") ? "[^/]+" : esc(seg)));
  return { re: new RegExp("^" + segs.join("/") + "/?$"), sub: sub ?? null };
}

/**
 * The guide for a page: entries whose route path matches; a route with #sub wins when the page's current tab is
 * that sub, else the route without a sub. Returns the entry id or null.
 */
export function resolveGuide(entries: ReadonlyArray<Pick<GuideEntry, "id" | "routes">>, pathname: string, sub: string | null): string | null {
  let best: { id: string; score: number } | null = null;
  for (const e of entries) for (const r of e.routes) {
    const p = routeParts(r);
    if (!p.re.test(pathname)) continue;
    const score = p.sub === null ? 1 : sub !== null && p.sub === sub ? 2 : 0;
    if (score && (!best || score > best.score)) best = { id: e.id, score };
  }
  return best?.id ?? null;
}

export interface GuideViewer { countries: string[]; native: boolean; manager: boolean }
export function guideLineVisible(l: GuideLine, v: GuideViewer): boolean {
  const countries = l.tags.filter((t) => t === "US" || t === "GB" || t === "MY" || t === "SG");
  if (countries.length && !countries.some((c) => v.countries.includes(c))) return false;
  if (l.tags.includes("web") && v.native) return false;
  if (l.tags.includes("app") && !v.native) return false;
  if (l.tags.includes("owner") && !v.manager) return false;
  return true;
}
export function guideBodyFor(e: GuideEntry, lang: GuideLang, v: GuideViewer): GuideBody {
  const b = e.body[lang];
  const out = emptyBody();
  for (const s of GUIDE_SECTIONS) out[s] = b[s].filter((l) => guideLineVisible(l, v));
  return out;
}

/** Store app bundle: only store-app routes, no web-only lines (applied at build time, see apps/web/vite.config.ts). */
/** front-desk pages that exist on the web only (apps/mobile/scripts/check-native-bundle.mjs forbids them in the app) */
export const GUIDE_WEB_ONLY_CHECKOUT = ["/checkout/member-centre", "/checkout/sales", "/checkout/approvals", "/checkout/daily-close", "/checkout/online-orders"];
export function guideForNative(e: GuideEntry): GuideEntry | null {
  const routes = e.routes.filter((r) => r.startsWith("/checkout/") && !GUIDE_WEB_ONLY_CHECKOUT.some((p) => r === p || r.startsWith(p + "/") || r.startsWith(p + "#")));
  if (!routes.length) return null;
  const body = {} as Record<GuideLang, GuideBody>;
  for (const l of GUIDE_LANGS) { body[l] = emptyBody(); for (const s of GUIDE_SECTIONS) body[l][s] = e.body[l][s].filter((x) => !x.tags.includes("web")); }
  return { ...e, routes, help: "", body };
}
